import * as React from 'react'
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from 'react-image-crop'
// The ReactCrop.css must be imported on project that installed this package
// import 'react-image-crop/dist/ReactCrop.css'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import { styled } from '../../theme/styled'
import { CloseIcon } from '../icons/CloseIcon'
import { CropImageModalProps } from './CropImageModalProps'

const getPreviewCrop = (image: any, crop: Crop) => {
  const canvas = document.createElement('canvas')
  const scaleX = image.naturalWidth / image.width
  const scaleY = image.naturalHeight / image.height
  canvas.width = crop.width
  canvas.height = crop.height

  const ctx = canvas.getContext('2d')

  ctx?.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height
  )
  const dataUrl = canvas.toDataURL()
  return dataUrl ? dataUrl : null
}

const CloseButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 12px;
`

const ModalButton = styled(Button)`
  margin-top: 10px;
`

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

export const CropImageModal = ({
  title,
  description,
  image,
  open,
  onCropImage,
  buttonText,
  onClose,
  inputName,
  aspectRatio = 1 / 1
}: CropImageModalProps) => {
  const [crop, setCrop] = React.useState<Crop>()

  const imgRef = React.useRef<HTMLImageElement | null>(null)
  const [croppedImage, setCroppedImage] = React.useState<any>(null)

  const onLoad = React.useCallback((img) => {
    setCrop(centerAspectCrop(img.width, img.height, aspectRatio))
  }, [])

  const handleCompletedCrop = async (crop: Crop) => {
    if (!imgRef.current) return

    const image = imgRef.current
    const previewUrl = getPreviewCrop(image, crop)

    if (previewUrl) {
      const croppedImageBlob = await fetch(previewUrl).then((res) => res.blob())
      const file = new File([croppedImageBlob], 'image.png', {
        type: 'image/png'
      })
      setCroppedImage(file)
    }
  }

  const handleClose = () => {
    setCroppedImage(null)
    onClose()
  }

  const handleCropImageButtonClick = () => {
    if (croppedImage) {
      const formData = new FormData()
      formData.append(inputName ? inputName : 'image', croppedImage)
      onCropImage(formData)
      handleClose()
    }
  }

  return (
    <Dialog open={open}>
      {!!image && (
        <React.Fragment>
          <DialogTitle>
            {title}
            <CloseButton aria-label='close' onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
          </DialogTitle>
          <DialogContent>
            {description && <Typography>{description}</Typography>}
            <ReactCrop
              aspect={aspectRatio}
              crop={crop}
              onChange={setCrop}
              onComplete={handleCompletedCrop}
            >
              <img src={image} onLoad={onLoad} ref={imgRef} />
            </ReactCrop>
          </DialogContent>
          <DialogActions>
            <ModalButton onClick={handleCropImageButtonClick}>
              {buttonText ? buttonText : 'Crop image'}
            </ModalButton>
          </DialogActions>
        </React.Fragment>
      )}
    </Dialog>
  )
}
